import { useState } from 'react'
import { useLang } from '../lang'
import type { CollageData } from '../types'
import * as cc from './collage.css'

// The Ruby-packed collage: nodes come from /api/overview (CollagePresenter), we
// just draw them. Hover captions a bird; clicking opens its modal.
// `status` shapes the empty-collage line: "listening" only reads true when the mic is actually
// up — an offline station is not listening, so it says so instead. The words are the SAME two
// the almanac strip and the empty-state line below use, in the reader's own language: this line
// used to be Irish whatever the reader had chosen, so an English page could say "ag éisteacht…"
// over "Listening" over "Listening — nothing heard yet.", three wordings for one state.
// The ellipsis belongs to LISTENING alone: it means "still going". Offline is not still going.
export function Collage({
  data,
  onSelect,
  status,
}: {
  data: CollageData
  onSelect: (sci: string) => void
  status: 'listening' | 'offline'
}) {
  const { t, lang } = useLang()
  const [cap, setCap] = useState<{ ga: string | null; en: string; count: number } | null>(null)
  // The reader's language first; the other one only when the station actually has it. A bird with
  // no Irish name collapses to one name rather than trailing an empty gloss (§7).
  const inIrish = (c: { ga: string | null }) => lang === 'ga' && !!c.ga
  const primary = (c: { ga: string | null; en: string }) => (inIrish(c) ? (c.ga as string) : c.en)
  const secondary = (c: { ga: string | null; en: string }) =>
    inIrish(c) ? c.en : lang === 'ga' ? null : c.ga

  return (
    <div className="ed-hero-collage">
      {/* Explicit width/height + aspect-ratio give the SVG a stable intrinsic size,
          so `height: auto` in a flex container can't collapse it to a line on a
          scroll-driven reflow. */}
      <svg
        className="collage"
        viewBox={`0 0 ${data.width} ${data.height}`}
        width={data.width}
        height={data.height}
        style={{ aspectRatio: `${data.width} / ${data.height}` }}
        xmlns="http://www.w3.org/2000/svg"
      >
        {data.species_count === 0 && (
          <text className={cc.emptyMark} x={data.width / 2} y={data.height / 2} textAnchor="middle">
            {status === 'offline' ? t('Offline', 'As líne') : t('Listening…', 'Ag éisteacht…')}
          </text>
        )}
        {data.nodes.map((n, i) => (
          <g
            key={`${n.sci}-${i}`}
            className={`collage__bird ${cc.bird}`}
            onMouseEnter={() => setCap({ ga: n.ga, en: n.en, count: n.count })}
            onMouseLeave={() => setCap(null)}
            onClick={() => onSelect(n.sci)}
          >
            {n.image ? (
              <image
                href={n.image}
                x={n.cx - n.w / 2}
                y={n.cy - n.h / 2}
                width={n.w}
                height={n.h}
                preserveAspectRatio="xMidYMid meet"
                transform={n.flip ? `translate(${(n.cx * 2).toFixed(2)} 0) scale(-1 1)` : undefined}
              />
            ) : (
              <circle cx={n.cx} cy={n.cy} r={n.r} fill={n.fill} />
            )}
          </g>
        ))}
      </svg>
      <p className="ed-tip" aria-hidden={!cap}>
        {cap && (
          <>
            {/* THE READER'S language leads, and the other trails as a gloss — the way a bird is
                named in the roll below, the Directory, the Stats table and the species sheet.
                This one caption named the bird in Irish first whatever language the page was in,
                so an English reader hovering a bird read "Sacán · Fieldfare". */}
            <span className="ct-name" lang={inIrish(cap) ? 'ga' : 'en'}>
              {primary(cap)}
            </span>
            {secondary(cap) && (
              <>
                {' '}
                <em className="ct-alt" lang={inIrish(cap) ? 'en' : 'ga'}>
                  {secondary(cap)}
                </em>
              </>
            )}
            {' · '}
            <span className="ct-n">{cap.count.toLocaleString()}</span>{' '}
            <span className="ct-w">{cap.count === 1 ? t('call', 'glaoch') : t('calls', 'glaonna')}</span>
          </>
        )}
      </p>
    </div>
  )
}
